// カルーセル共通エンジン（無限ループ・矢印送り・スワイプ／フリック対応）。
// 画像カルーセル（carousel.js）とピックアップ／関連記事カルーセル（pickupCarousel.js）の
// 実装をこの 1 ファイルに統合し、両者は config を渡す薄いラッパーになる。
//
// アニメーションは CSS transition ではなく、定番ライブラリ Flickity と同方式の
// 物理シミュレーション（requestAnimationFrame ループ＋吸着力＋摩擦）で行う。
//
// - ドラッグ中: 毎フレーム velocity = dragX - x で指に完全追従（Flickity の applyDragForce）。
//   位置と速度を常に自前で持つので、「走行中に掴み直す」「矢印を連打する」が特別処理なしで
//   連続的に繋がる（旧実装の transitionend 監視・走行位置の再取得・再基点計算が不要になる）。
//   指を止めれば velocity は自然に 0 へ減衰するので、フリック速度の実測サンプリングも不要。
// - 離した後: 目標スライドへ「残距離 × SELECTED_ATTRACTION」の力を毎フレーム加え、
//   FRICTION で減衰させて滑らかに吸着する（Flickity の applySelectedAttraction）。
// - 送り先の決定（Flickity の dragEndRestingSelect ＋ dragEndBoostSelect）:
//   慣性まかせで自然に止まるはずの位置 restingX = x + velocity / FRICTION に最も近い
//   スライドを選ぶ。速いスワイプは複数コマ先まで飛び、ゆっくり離せば最寄りへ収まる。
//   送り先が現在のスライドのままでも、離す直前 BOOST_MS 以内に進行方向へ動いて
//   いれば 1 コマ送る（短く素早いフリックを取りこぼさないための救済）。
// - 縦スクロールとの共存: 軸判定はしない。横へ DRAG_START_PX 動いた時点でドラッグを
//   開始するだけ。縦方向は touch-action: pan-y でブラウザが引き受け、ブラウザが
//   スクロールを始めると pointercancel が届くのでそこでドラッグを終える（Flickity と同じ）。
// - 無限ループ: 実スライド集合を前後へ 1 セットずつ全クローンし、トラックを
//   [clone×total][real×total][clone×total] にする。座標 x は折り返さず無限に伸ばし、
//   描画時にだけ実スライド帯へ剰余で折り返す。クローンと実の見た目が同一なので
//   継ぎ目は出ない（旧実装の transitionend 後の瞬間移動が不要になる）。
// - 静的化: 全スライドがビューポートに収まる場合は is-static を付けて静的表示にする
//   （CSS 側でクローン・ナビを隠す）。

import { onEnterView } from './_inView';

// --- 操作感の調整値 ---
const SELECTED_ATTRACTION = 0.025; // 目標スライドへ引き寄せる力（残距離に対する割合/歩）。大きいほど機敏に到達する。Flickity 既定値
const FRICTION = 0.28;             // 速度の減衰率（1 歩ごと）。大きいほど早く止まりキビキビする。Flickity 既定値
const DRAG_START_PX = 3;           // px。横にこの距離動いたらドラッグ開始（タップ扱いをやめ、直後のクリックも抑止）。Flickity 既定値
const BOOST_MS = 100;              // ms。離す直前この時間内に進行方向へ動いていれば、距離が足りなくても 1 コマ送る
const EDGE_RESISTANCE = 0.5;       // loop:false の端を越えて引っ張るときの抵抗。0.5 ＝ 指の半分だけ付いてくる。Flickity と同じ式
const RESIZE_DEBOUNCE_MS = 100;    // ms。連続するウィンドウリサイズが落ち着いてから寸法を再計算する
const MOUSE_FLICK_WINDOW_MS = 80;  // ms。マウスは離す瞬間に手が止まり最終フレームの速度が0近くになりがち。離す直前この時間の平均移動速度を放り投げ初速に採用し、指スワイプと同じ慣性で滑らせる
const FRAME_MS = 1000 / 60;        // ms。物理1歩の想定フレーム時間。上の平均速度(px/ms)を物理の速度単位(px/歩)へ換算するのに使う

/**
 * 1 つのカルーセル要素を初期化する。
 * @param {HTMLElement} root - カルーセルのルート要素
 * @param {Object} config
 *   trackSelector / viewportSelector / prevSelector / nextSelector / counterSelector
 *   loop {boolean} 無限ループ（既定 true）。false は端で停止・繰り返さない
 *   onClone {(clone:HTMLElement)=>void} クローン生成時の追加処理（省略可）
 *   onChange {(index:number)=>void} 選択スライドが変わったとき、実スライド番号（0 始まり）で呼ぶ（省略可）
 *   measureStep {(ctx)=>number} 1 コマの送り量(px)を返す。ctx={root,track,viewport,slides}
 */
export function createCarousel(root, config) {
  const track = root.querySelector(config.trackSelector);
  if (!track) return;
  const viewport = config.viewportSelector ? root.querySelector(config.viewportSelector) : null;
  const counter = config.counterSelector ? root.querySelector(config.counterSelector) : null;
  const prevBtn = root.querySelector(config.prevSelector);
  const nextBtn = root.querySelector(config.nextSelector);
  if (counter) {
    counter.setAttribute('role', 'status');
    counter.setAttribute('aria-live', 'polite');
    counter.setAttribute('aria-atomic', 'true');
  }

  const slides = Array.from(track.children);
  const total = slides.length;

  // スライドが 1 枚以下ならループもスワイプも不要。
  if (total <= 1) {
    if (counter) counter.textContent = total ? `1 / ${total}` : '';
    if (prevBtn) prevBtn.disabled = true;
    if (nextBtn) nextBtn.disabled = true;
    root.classList.add('is-static', 'is-init');
    return;
  }

  // 既定は無限ループ。config.loop:false で繰り返し無効（クローンせず端で停止）。
  const loop = config.loop !== false;

  // --- 無限ループ用に実集合を前後へ 1 セットずつ全クローン（loop 時のみ） ---
  // トラック構成: [clone×total][real×total][clone×total]
  if (loop) {
    const makeCloneSet = () => slides.map((el) => {
      const clone = el.cloneNode(true);
      clone.classList.add('is-clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((focusable) => {
        focusable.setAttribute('tabindex', '-1');
      });
      if (config.onClone) config.onClone(clone);
      return clone;
    });
    makeCloneSet().forEach((c) => track.insertBefore(c, slides[0]));
    makeCloneSet().forEach((c) => track.appendChild(c));
  }

  // --- 状態 ---
  // x はトラックの論理位置(px)。実スライド i の定位置は -i*step。
  // loop 時は折り返さず無限に伸ばし、render() で描画時にだけ実スライド帯へ剰余変換する。
  let step = 0;        // 1 コマの送り量(px)
  let x = 0;           // 現在位置(px)
  let velocity = 0;    // 速度(px/物理1歩)
  let selected = 0;    // 吸着目標のスライド位置。loop 時は無限整数、loop:false 時は [0, maxPos]
  let maxPos = 0;      // loop:false 時の selected 上限（右端）
  let enabled = false; // カルーセルが有効か（全スライドが収まらないとき true）

  const measureStep = () => config.measureStep({ root, track, viewport, slides });
  const targetX = () => -selected * step;

  // 描画。loop 時は x を実スライド帯 (-2*totalW, -totalW] へ剰余で折り返してから描く。
  // トラックには 3 セット分あり、enabled 時は totalW > ビューポート幅が保証されるので、
  // この帯に収めれば左右どちらの端もクローンで埋まり継ぎ目が出ない。
  const render = () => {
    let rx = x;
    if (loop) {
      const w = total * step;
      rx = -((((-x) % w) + w) % w) - w;
    }
    track.style.transform = `translate3d(${rx}px, 0, 0)`;
  };

  // いま何枚見えているか（loop:false の端計算・カウンター表示用）。
  const visibleCount = () => {
    const viewportW = (viewport || root).clientWidth;
    return step > 0 ? Math.max(1, Math.floor(viewportW / step)) : 1;
  };
  // loop:false 時の selected 上限（右端）。最後の「見えるカード数」分だけ手前で止める。
  const computeMaxPos = () => Math.max(0, total - visibleCount());

  // 数字ナビ "現在 / 総数"。
  // loop 時は剰余で先頭カード番号。loop:false 時は「いま見えている右端カード番号 / 総数」
  // （複数枚表示でも端まで送ると N/N に達するようにするための表示）。
  const updateCounter = () => {
    // カウンターと同じタイミングで選択中の実スライド番号を通知する（loop 時は剰余で折り返す）。
    if (config.onChange) config.onChange(loop ? ((selected % total) + total) % total : selected);
    if (!counter) return;
    if (loop) {
      const index = ((selected % total) + total) % total;
      counter.textContent = `${index + 1} / ${total}`;
    } else {
      const shown = Math.min(selected + visibleCount(), total);
      counter.textContent = `${shown} / ${total}`;
    }
    if (!loop) {
      const max = computeMaxPos();
      if (prevBtn) prevBtn.disabled = selected <= 0;
      if (nextBtn) nextBtn.disabled = selected >= max;
    }
  };

  // --- 物理ループ ---
  let pointerDown = false; // 指・ボタンが押されているか（押下中は吸着を止め位置を指に委ねる）
  let dragging = false;    // ドラッグが始まったか（DRAG_START_PX 超）
  let dragX = 0;           // ドラッグの目標位置(px)。押下中はここへ毎歩追従する
  let raf = 0;

  // 物理 1 歩。Flickity の applyDragForce / applySelectedAttraction / integratePhysics と同順。
  const physicsStep = () => {
    if (pointerDown) {
      velocity = dragX - x; // 1 歩で指の位置へ追いつく（離した瞬間、直前の移動量がそのまま初速になる）
    } else {
      velocity += (targetX() - x) * SELECTED_ATTRACTION;
    }
    x += velocity;
    velocity *= (1 - FRICTION);
  };

  // rAF 1 フレームにつき物理 1 歩（Flickity と同じ）。
  // 固定刻み＋蓄積方式にすると、タイムスタンプの揺らぎで「1 歩も進まないフレーム」が
  // 生まれ、ドラッグ追従が数フレームおきに止まって見える（ジャダー）ので採らない。
  const tick = () => {
    raf = 0;
    if (!enabled) return;
    physicsStep();
    // 静止判定: 目標に十分近く速度も出ていなければ、目標へスナップしてループを止める。
    if (!pointerDown && Math.abs(targetX() - x) < 0.5 && Math.abs(velocity) < 0.5) {
      if (loop) selected = ((selected % total) + total) % total; // 座標の無限成長を防ぐ
      x = targetX();
      velocity = 0;
      render();
      return;
    }
    render();
    raf = requestAnimationFrame(tick);
  };

  const startLoop = () => {
    if (raf) return;
    raf = requestAnimationFrame(tick);
  };

  // 吸着目標を変えてアニメーションを開始する。走行中でも位置・速度は連続する。
  const select = (index) => {
    if (!enabled) return;
    selected = loop ? index : Math.max(0, Math.min(index, computeMaxPos()));
    updateCounter();
    startLoop();
  };

  if (prevBtn) prevBtn.addEventListener('click', (e) => { e.preventDefault(); select(selected - 1); });
  if (nextBtn) nextBtn.addEventListener('click', (e) => { e.preventDefault(); select(selected + 1); });

  // --- スワイプ（Pointer Events：マウス／タッチ共通） ---
  let pointerId = null;    // 追跡中のポインタ。2 本目以降の指は無視する
  let moved = false;       // ドラッグしたか（直後の click でのリンク遷移／拡大を抑止）
  let downX = 0;           // pointerdown 時の座標（ドラッグ開始判定の基点）
  let dragStartX = 0;      // pointerdown 時の x（ドラッグ量はここからの相対）
  let prevDragX = 0;       // 直前 move 時の dragX（ブースト判定の方向検出用）
  let dragMoveTime = 0;    // 最後に指が動いた時刻（ブースト判定用）
  let downPointerType = null; // 押下したポインタ種別（'mouse' のとき離したときの慣性を補う）
  const flickSamples = [];    // マウス時のみ記録する {t, dragX} 履歴。離す直前の平均速度算出に使う

  track.addEventListener('dragstart', (e) => e.preventDefault());

  track.addEventListener('pointerdown', (e) => {
    if (pointerDown) return; // 2 本目以降の指は無視（moved も維持する）
    // 新しい操作の開始時にクリック抑止フラグを下ろす。直前のスワイプが pointercancel 等で
    // trailing click を伴わず終わっても、次のタップが握りつぶされないようにする。
    moved = false;
    if (!enabled) return;
    if (e.pointerType === 'mouse') {
      if (e.button !== 0) return; // 左ボタンのみ
      e.preventDefault();         // テキスト選択・画像のネイティブドラッグを抑止（Flickity と同じ）
    }
    pointerDown = true;
    pointerId = e.pointerId;
    dragging = false;
    step = measureStep() || step;
    maxPos = computeMaxPos();
    downX = e.clientX;
    // 押下した瞬間から位置を指に委ねる＝走行中でもその場でピタッと止まる（Flickity と同じ）。
    dragX = dragStartX = x;
    prevDragX = x;
    dragMoveTime = 0;
    downPointerType = e.pointerType;
    flickSamples.length = 0;
    track.classList.add('is-grabbing');
    startLoop();
  });

  // 移動・終了は window で受ける：カーソルがコンテナ外・ウィンドウ外でも確実に拾うため。
  // タッチはブラウザの implicit pointer capture が押下要素へ届け続けたものが window へ届く。
  window.addEventListener('pointermove', (e) => {
    if (!pointerDown || e.pointerId !== pointerId) return;
    // ボタンが押されていないマウス移動＝どこかで離上を取りこぼした → ドラッグ終了。
    if (e.pointerType === 'mouse' && e.buttons === 0) { endDrag(); return; }
    const delta = e.clientX - downX;
    if (!dragging) {
      // 横に DRAG_START_PX 動いたらドラッグ開始。縦スクロールの軸判定はしない
      //（縦は touch-action: pan-y でブラウザが引き受け、pointercancel で終了が届く）。
      if (Math.abs(delta) <= DRAG_START_PX) return;
      dragging = true;
      moved = true;
    }
    let nx = dragStartX + delta;
    if (!loop) {
      // 端を越えて引っ張ったら抵抗を付ける（Flickity の dragMove と同じ式）。
      const originBound = Math.max(0, dragStartX);
      if (nx > originBound) nx = (nx + originBound) * EDGE_RESISTANCE;
      const endBound = Math.min(-maxPos * step, dragStartX);
      if (nx < endBound) nx = (nx + endBound) * EDGE_RESISTANCE;
    }
    prevDragX = dragX;
    dragX = nx;
    dragMoveTime = performance.now();
    // マウスは離す直前の平均速度で慣性を出すため、ドラッグ位置の履歴を残す。
    if (downPointerType === 'mouse') flickSamples.push({ t: dragMoveTime, x: dragX });
  });

  // 横ドラッグ確定後は、指を離すまで縦移動でもネイティブスクロールを発動させない。
  // touch-action: pan-y はジェスチャ途中でも縦パンの開始をブラウザに許すため、
  // ドラッグ中の touchmove を preventDefault で打ち消す（passive:false が必須）。
  // ドラッグ確定前（タップや縦スクロール）は打ち消さないので、縦は今までどおり
  // ネイティブスクロールに流れる。cancelable=false はブラウザがスクロール中の
  // ジェスチャで、打ち消せないので触らない（コンソール警告も避ける）。
  track.addEventListener('touchmove', (e) => {
    if (dragging && e.cancelable) e.preventDefault();
  }, { passive: false });

  const endDrag = () => {
    if (!pointerDown) return;
    pointerDown = false;
    pointerId = null;
    track.classList.remove('is-grabbing');
    if (!dragging) return; // タップ：吸着へ戻すだけ（click はネイティブに任せる）
    dragging = false;
    // マウスは離す直前に手が止まって最終フレームの velocity が 0 近くになり慣性が乗らない。
    // 指スワイプと同じ滑りにするため、離す直前 MOUSE_FLICK_WINDOW_MS の平均移動速度から
    // 初速を求め、最終フレーム由来の velocity より速ければそちらを採用する。
    // 直前まで動き続けていた場合だけ補い、止めて置いた（＝ドロップ）ときは吸着のままにする。
    if (downPointerType === 'mouse' && flickSamples.length) {
      const now = performance.now();
      const last = flickSamples[flickSamples.length - 1];
      if (now - last.t <= MOUSE_FLICK_WINDOW_MS) {
        let ref = flickSamples[0];
        for (let i = flickSamples.length - 1; i >= 0; i--) {
          if (flickSamples[i].t <= now - MOUSE_FLICK_WINDOW_MS) { ref = flickSamples[i]; break; }
        }
        const dt = now - ref.t;
        if (dt > 0) {
          const windowVelocity = ((dragX - ref.x) / dt) * FRAME_MS * (1 - FRICTION);
          if (Math.abs(windowVelocity) > Math.abs(velocity)) velocity = windowVelocity;
        }
      }
    }
    // 送り先の決定（Flickity の dragEndRestingSelect）:
    // 慣性で自然に止まるはずの位置に最も近いスライドへ吸着する。
    const restingX = x + velocity / FRICTION;
    let index = Math.round(-restingX / step);
    if (index === selected && dragMoveTime && performance.now() - dragMoveTime < BOOST_MS) {
      // 送り先が変わらない場合でも、離す直前に進行方向へ動いていれば 1 コマ送る
      //（Flickity の dragEndBoostSelect。短く素早いフリックの取りこぼし防止）。
      const delta = prevDragX - dragX;            // 直前の移動量（正＝次へ）
      const distance = -dragX - selected * step;  // 現在スライドからの行き過ぎ量（正＝次へ）
      if (distance > 0 && delta > 0) index += 1;
      else if (distance < 0 && delta < 0) index -= 1;
    }
    select(index);
  };

  window.addEventListener('pointerup', (e) => {
    if (pointerDown && e.pointerId === pointerId) endDrag();
  });
  // ブラウザが縦スクロールを開始した等。位置は慣性判定に任せる（ほぼ動いていなければ元へ戻る）。
  window.addEventListener('pointercancel', (e) => {
    if (pointerDown && e.pointerId === pointerId) endDrag();
  });

  // ドラッグ直後のクリックはリンク遷移／PhotoSwipe 起動を抑止（capture フェーズ）。
  // 純粋なタップは何もせずネイティブの click に任せる（pointer capture を使っていないので
  // click は押下した要素へそのまま届く）。
  track.addEventListener('click', (e) => {
    if (!moved) return;
    e.preventDefault();
    e.stopPropagation();
    moved = false;
  }, true);

  // 全スライドが収まるかで有効/無効を切り替える。
  // 収まる場合は is-static を付け、クローン・ナビを CSS で隠し静的表示にする。
  // 判定の基準幅は track の幅。カード幅は track に対する % で決まるうえ、
  // track が左右に負のマージンを持つ場合（ピックアップ）は viewport 幅と一致しないため。
  const evaluate = () => {
    step = measureStep();
    const laneW = track.clientWidth || (viewport || root).clientWidth;
    enabled = step > 0 && total * step > laneW + 1;
    root.classList.toggle('is-static', !enabled);
    // 操作途中の再評価（リサイズ等）はドラッグを打ち切って整合を取る。
    pointerDown = false;
    pointerId = null;
    dragging = false;
    moved = false;
    track.classList.remove('is-grabbing');
    velocity = 0;
    if (enabled) {
      if (loop) selected = ((selected % total) + total) % total;
      else selected = Math.max(0, Math.min(selected, computeMaxPos()));
      x = targetX();
      render();
      updateCounter();
    } else {
      // 静的表示：クローンは CSS で非表示。実スライドを左端から並べる。
      x = 0;
      selected = 0;
      track.style.transform = 'translate3d(0, 0, 0)';
      if (prevBtn) prevBtn.disabled = true;
      if (nextBtn) nextBtn.disabled = true;
    }
  };

  // リサイズ：step（ビューポート幅／カード幅）と収まり判定が変わるため再評価。
  // Basicテンプレートのように複数カルーセルがあるページで、リサイズ中の毎フレームに
  // 各カルーセルがレイアウト計測とDOM更新を繰り返さないよう、横幅変更をデバウンスする。
  let lastWindowWidth = document.documentElement.clientWidth;
  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    const currentWindowWidth = document.documentElement.clientWidth;
    if (currentWindowWidth === lastWindowWidth) return;
    lastWindowWidth = currentWindowWidth;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      resizeTimer = 0;
      evaluate();
    }, RESIZE_DEBOUNCE_MS);
  });

  // 物理アニメと二重にかからないよう、transition は明示的に無効化しておく。
  track.style.transition = 'none';

  // 初期評価。画像読み込み等で初期幅が 0 のことがあるため、進入時にも再評価。
  evaluate();
  onEnterView(root, evaluate);

  root.classList.add('is-init');
}
